import { z } from "zod";

export const OPERATING_PREFERENCE_PROFILE_SCHEMA_VERSION =
	"operating_preference_profile.v1" as const;

export const OperatingPreferenceCompletionLevelSchema = z.enum([
	"starter",
	"personalized",
	"optimized",
]);
export type OperatingPreferenceCompletionLevel = z.infer<
	typeof OperatingPreferenceCompletionLevelSchema
>;

export const OperatingPreferenceFieldStatusSchema = z.enum([
	"declared",
	"inferred",
	"defaulted",
	"confirmed",
	"edited_by_user",
]);
export type OperatingPreferenceFieldStatus = z.infer<
	typeof OperatingPreferenceFieldStatusSchema
>;

export const OperatingPreferencePatchSourceSchema = z.enum([
	"card_answer",
	"user_freeform",
	"ai_inference",
	"default",
	"user_edit",
	"review_adjustment",
]);
export type OperatingPreferencePatchSource = z.infer<
	typeof OperatingPreferencePatchSourceSchema
>;

export const OperatingPreferenceFieldSourceSchema = z
	.object({
		confidence: z.number().min(0).max(1),
		source: OperatingPreferencePatchSourceSchema,
		status: OperatingPreferenceFieldStatusSchema,
		updatedAt: z.string().datetime({ offset: true }),
	})
	.strict();
export type OperatingPreferenceFieldSource = z.infer<
	typeof OperatingPreferenceFieldSourceSchema
>;

export const OperatingPreferencePrimaryIntentSchema = z.enum([
	"build_wealth",
	"earn_on_cash",
	"trade_actively",
	"learn",
	"send_or_spend",
	"try_crypto",
	"preserve_value",
	"save_for_goal",
	"diversify",
	"generate_income",
]);
export type OperatingPreferencePrimaryIntent = z.infer<
	typeof OperatingPreferencePrimaryIntentSchema
>;

export const OperatingPreferenceSecondaryIntentSchema =
	OperatingPreferencePrimaryIntentSchema;
export type OperatingPreferenceSecondaryIntent = z.infer<
	typeof OperatingPreferenceSecondaryIntentSchema
>;

export const OperatingPreferenceGoalTypeSchema = z.enum([
	"long_term_growth",
	"short_term_savings",
	"emergency_buffer",
	"house_or_large_purchase",
	"income_generation",
	"crypto_exposure",
	"cash_yield",
	"learn_first",
	"active_trading",
	"wealth_preservation",
]);
export type OperatingPreferenceGoalType = z.infer<
	typeof OperatingPreferenceGoalTypeSchema
>;

export const OperatingPreferenceTimeHorizonSchema = z.enum([
	"soon",
	"one_to_three_years",
	"multi_year",
	"just_exploring",
]);
export type OperatingPreferenceTimeHorizon = z.infer<
	typeof OperatingPreferenceTimeHorizonSchema
>;

export const OperatingPreferenceFundingBehaviorSchema = z.enum([
	"one_time_large",
	"small_recurring",
	"paycheck_based",
	"manual_only",
	"not_ready_to_fund",
	"exploring_first",
]);
export type OperatingPreferenceFundingBehavior = z.infer<
	typeof OperatingPreferenceFundingBehaviorSchema
>;

export const OperatingPreferenceGuidanceLevelSchema = z.enum([
	"self_directed",
	"guided",
	"autopilot",
]);
export type OperatingPreferenceGuidanceLevel = z.infer<
	typeof OperatingPreferenceGuidanceLevelSchema
>;

export const OperatingPreferenceAutomationLevelSchema = z.enum([
	"manual",
	"suggested_with_confirmation",
	"automatic_for_low_risk_actions",
]);
export type OperatingPreferenceAutomationLevel = z.infer<
	typeof OperatingPreferenceAutomationLevelSchema
>;

export const OperatingPreferenceDecisionStyleSchema = z.enum([
	"tell_me_what_to_do",
	"show_me_options",
	"i_will_choose",
]);
export type OperatingPreferenceDecisionStyle = z.infer<
	typeof OperatingPreferenceDecisionStyleSchema
>;

export const OperatingPreferenceInterfaceComplexitySchema = z.enum([
	"simple",
	"standard",
	"advanced",
]);
export type OperatingPreferenceInterfaceComplexity = z.infer<
	typeof OperatingPreferenceInterfaceComplexitySchema
>;

export const OperatingPreferenceEducationDepthSchema = z.enum([
	"none",
	"plain_english",
	"detailed",
	"expert",
]);
export type OperatingPreferenceEducationDepth = z.infer<
	typeof OperatingPreferenceEducationDepthSchema
>;

export const OperatingPreferenceVolatilityLevelSchema = z.enum([
	"low",
	"low_to_medium",
	"medium",
	"high",
	"very_high",
]);
export type OperatingPreferenceVolatilityLevel = z.infer<
	typeof OperatingPreferenceVolatilityLevelSchema
>;

export const OperatingPreferenceDrawdownComfortSchema = z.enum([
	"less_than_5_percent",
	"5_to_10_percent",
	"10_to_20_percent",
	"20_percent_plus",
	"not_sure",
]);
export type OperatingPreferenceDrawdownComfort = z.infer<
	typeof OperatingPreferenceDrawdownComfortSchema
>;

export const OperatingPreferenceDropReactionSchema = z.enum([
	"panic_sell",
	"wait_and_watch",
	"buy_more",
	"not_sure",
]);
export type OperatingPreferenceDropReaction = z.infer<
	typeof OperatingPreferenceDropReactionSchema
>;

export const OperatingPreferenceExperienceLevelSchema = z.enum([
	"beginner",
	"some",
	"advanced",
	"active_trader",
]);
export type OperatingPreferenceExperienceLevel = z.infer<
	typeof OperatingPreferenceExperienceLevelSchema
>;

export const OperatingPreferenceAssetFamiliaritySchema = z.enum([
	"none",
	"curious",
	"some",
	"comfortable",
	"expert",
]);
export type OperatingPreferenceAssetFamiliarity = z.infer<
	typeof OperatingPreferenceAssetFamiliaritySchema
>;

export const OperatingPreferenceAssetInterestSchema = z.enum([
	"cash",
	"cash_yield",
	"stocks",
	"etfs",
	"crypto",
	"stablecoins",
	"tokenized_assets",
	"options",
	"prediction_markets",
	"private_markets",
]);
export type OperatingPreferenceAssetInterest = z.infer<
	typeof OperatingPreferenceAssetInterestSchema
>;

export const OperatingPreferenceFeatureInterestSchema = z.enum([
	"recurring_deposits",
	"auto_portfolio",
	"watchlist",
	"price_alerts",
	"copy_trading",
	"education",
	"news_digest",
	"cash_management",
	"card_spend",
	"wallet_transfers",
	"yield",
]);
export type OperatingPreferenceFeatureInterest = z.infer<
	typeof OperatingPreferenceFeatureInterestSchema
>;

export const OperatingPreferenceCryptoInterestSchema = z.enum([
	"none",
	"curious_but_cautious",
	"interested",
	"crypto_native",
	"avoid",
]);
export type OperatingPreferenceCryptoInterest = z.infer<
	typeof OperatingPreferenceCryptoInterestSchema
>;

export const OperatingPreferenceExcludedInterestSchema = z.enum([
	"options",
	"leverage",
	"memecoins",
	"high_volatility_crypto",
	"prediction_markets",
	"private_markets",
]);
export type OperatingPreferenceExcludedInterest = z.infer<
	typeof OperatingPreferenceExcludedInterestSchema
>;

export const OperatingPreferenceBalanceDisplaySchema = z.enum([
	"fiat_first",
	"crypto_native",
	"hybrid",
]);
export type OperatingPreferenceBalanceDisplay = z.infer<
	typeof OperatingPreferenceBalanceDisplaySchema
>;

export const OperatingPreferenceWalletTransferVisibilitySchema = z.enum([
	"hidden_until_requested",
	"visible",
	"advanced_only",
]);
export type OperatingPreferenceWalletTransferVisibility = z.infer<
	typeof OperatingPreferenceWalletTransferVisibilitySchema
>;

export const OperatingPreferenceStablecoinLanguageSchema = z.enum([
	"plain_english",
	"technical",
]);
export type OperatingPreferenceStablecoinLanguage = z.infer<
	typeof OperatingPreferenceStablecoinLanguageSchema
>;

export const OperatingPreferenceToneSchema = z.enum([
	"friendly",
	"direct",
	"analytical",
	"minimal",
]);
export type OperatingPreferenceTone = z.infer<
	typeof OperatingPreferenceToneSchema
>;

export const OperatingPreferenceCoachingStyleSchema = z.enum([
	"encouraging",
	"direct",
	"analytical",
	"minimal",
	"hype",
	"teacher",
]);
export type OperatingPreferenceCoachingStyle = z.infer<
	typeof OperatingPreferenceCoachingStyleSchema
>;

export const OperatingPreferenceNotificationCadenceSchema = z.enum([
	"quiet",
	"important_only",
	"weekly_digest",
	"active_alerts",
	"real_time",
]);
export type OperatingPreferenceNotificationCadence = z.infer<
	typeof OperatingPreferenceNotificationCadenceSchema
>;

export const OperatingPreferencePreferredChannelSchema = z.enum([
	"push",
	"email",
	"in_app",
]);
export type OperatingPreferencePreferredChannel = z.infer<
	typeof OperatingPreferencePreferredChannelSchema
>;

export const OperatingPreferenceDigestFrequencySchema = z.enum([
	"none",
	"weekly",
	"daily",
]);
export type OperatingPreferenceDigestFrequency = z.infer<
	typeof OperatingPreferenceDigestFrequencySchema
>;

export const OperatingPreferenceArchetypeSchema = z.enum([
	"autopilot_builder",
	"curious_explorer",
	"active_trader",
	"cash_optimizer",
	"crypto_native",
	"steady_stacker",
	"yield_seeker",
	"learn_first_beginner",
]);
export type OperatingPreferenceArchetype = z.infer<
	typeof OperatingPreferenceArchetypeSchema
>;

export const OperatingPreferenceLocalizationSchema = z
	.object({
		country: z.string().min(2).max(2).nullable(),
		region: z.string().min(1).max(16).nullable(),
		currency: z.string().min(3).max(3),
		language: z.string().min(2).max(16),
		timezone: z.string().min(1).max(64).nullable(),
		source: z.enum(["inferred", "declared", "defaulted", "confirmed"]),
	})
	.strict();
export type OperatingPreferenceLocalization = z.infer<
	typeof OperatingPreferenceLocalizationSchema
>;

export const OperatingPreferenceIntentSchema = z
	.object({
		freeform: z.string().trim().max(1_000).nullable(),
		primary: OperatingPreferencePrimaryIntentSchema.nullable(),
		secondary: z.array(OperatingPreferenceSecondaryIntentSchema).max(5),
	})
	.strict();
export type OperatingPreferenceIntent = z.infer<
	typeof OperatingPreferenceIntentSchema
>;

const OptionalMoneyAmountSchema = z
	.object({
		amount: z
			.string()
			.regex(/^\d+(\.\d{1,2})?$/u)
			.nullable(),
		currency: z.string().min(3).max(3),
		isEstimate: z.boolean(),
	})
	.strict();

const OptionalRecurringMoneyAmountSchema = OptionalMoneyAmountSchema.extend({
	cadence: z.enum(["weekly", "biweekly", "monthly"]).nullable(),
	enabled: z.boolean(),
});

export const OperatingPreferenceGoalSchema = z
	.object({
		id: z.string().min(1).max(80),
		label: z.string().min(1).max(160),
		monthlyContribution: z
			.string()
			.regex(/^\d+(\.\d{1,2})?$/u)
			.nullable(),
		priority: z.number().int().positive(),
		targetAmount: z
			.string()
			.regex(/^\d+(\.\d{1,2})?$/u)
			.nullable(),
		timeHorizon: OperatingPreferenceTimeHorizonSchema.nullable(),
		type: OperatingPreferenceGoalTypeSchema,
	})
	.strict();
export type OperatingPreferenceGoal = z.infer<
	typeof OperatingPreferenceGoalSchema
>;

export const OperatingPreferenceMoneyRhythmSchema = z
	.object({
		preferredFundingBehavior: OperatingPreferenceFundingBehaviorSchema,
		recurringAmount: OptionalRecurringMoneyAmountSchema,
		startingAmount: OptionalMoneyAmountSchema,
	})
	.strict();
export type OperatingPreferenceMoneyRhythm = z.infer<
	typeof OperatingPreferenceMoneyRhythmSchema
>;

export const OperatingPreferenceProductModeSchema = z
	.object({
		automationLevel: OperatingPreferenceAutomationLevelSchema,
		decisionStyle: OperatingPreferenceDecisionStyleSchema,
		educationDepth: OperatingPreferenceEducationDepthSchema,
		guidanceLevel: OperatingPreferenceGuidanceLevelSchema,
		interfaceComplexity: OperatingPreferenceInterfaceComplexitySchema,
	})
	.strict();
export type OperatingPreferenceProductMode = z.infer<
	typeof OperatingPreferenceProductModeSchema
>;

export const OperatingPreferenceVolatilityComfortSchema = z
	.object({
		level: OperatingPreferenceVolatilityLevelSchema.nullable(),
		maxDrawdownComfort: OperatingPreferenceDrawdownComfortSchema.nullable(),
		preferenceLabel: z.string().min(1).max(80).nullable(),
		reactionToDrop: OperatingPreferenceDropReactionSchema.nullable(),
	})
	.strict();
export type OperatingPreferenceVolatilityComfort = z.infer<
	typeof OperatingPreferenceVolatilityComfortSchema
>;

export const OperatingPreferenceExperienceSchema = z
	.object({
		assetFamiliarity: z
			.object({
				crypto: OperatingPreferenceAssetFamiliaritySchema,
				etfs: OperatingPreferenceAssetFamiliaritySchema,
				options: OperatingPreferenceAssetFamiliaritySchema,
				stablecoins: OperatingPreferenceAssetFamiliaritySchema,
				stocks: OperatingPreferenceAssetFamiliaritySchema,
			})
			.strict(),
		overall: OperatingPreferenceExperienceLevelSchema.nullable(),
		usedApps: z.array(z.string().min(1).max(80)).max(12),
	})
	.strict();
export type OperatingPreferenceExperience = z.infer<
	typeof OperatingPreferenceExperienceSchema
>;

export const OperatingPreferenceInterestsSchema = z
	.object({
		assets: z.array(OperatingPreferenceAssetInterestSchema).max(12),
		cryptoInterest: OperatingPreferenceCryptoInterestSchema,
		excluded: z.array(OperatingPreferenceExcludedInterestSchema).max(8),
		features: z.array(OperatingPreferenceFeatureInterestSchema).max(12),
	})
	.strict();
export type OperatingPreferenceInterests = z.infer<
	typeof OperatingPreferenceInterestsSchema
>;

export const OperatingPreferenceCryptoRailsPresentationSchema = z
	.object({
		balanceDisplay: OperatingPreferenceBalanceDisplaySchema,
		cryptoNativeMode: z.boolean(),
		showNetworkNames: z.boolean(),
		showOnchainDetails: z.boolean(),
		stablecoinLanguage: OperatingPreferenceStablecoinLanguageSchema,
		walletTransfers: OperatingPreferenceWalletTransferVisibilitySchema,
	})
	.strict();
export type OperatingPreferenceCryptoRailsPresentation = z.infer<
	typeof OperatingPreferenceCryptoRailsPresentationSchema
>;

export const OperatingPreferenceCommunicationStyleSchema = z
	.object({
		coachingStyle: OperatingPreferenceCoachingStyleSchema,
		digestFrequency: OperatingPreferenceDigestFrequencySchema,
		notificationCadence: OperatingPreferenceNotificationCadenceSchema,
		preferredChannels: z
			.array(OperatingPreferencePreferredChannelSchema)
			.max(3),
		tone: OperatingPreferenceToneSchema,
	})
	.strict();
export type OperatingPreferenceCommunicationStyle = z.infer<
	typeof OperatingPreferenceCommunicationStyleSchema
>;

export const OperatingPreferenceArchetypeStateSchema = z
	.object({
		confidence: z.number().min(0).max(1),
		current: OperatingPreferenceArchetypeSchema.nullable(),
		visibleToUser: z.boolean(),
	})
	.strict();
export type OperatingPreferenceArchetypeState = z.infer<
	typeof OperatingPreferenceArchetypeStateSchema
>;

export const OperatingPreferenceAiStateSchema = z
	.object({
		confidence: z.number().min(0).max(1),
		declaredFields: z.array(z.string().min(1)).max(80),
		inferredFields: z.array(z.string().min(1)).max(80),
		lastUpdatedAt: z.string().datetime({ offset: true }),
		missingHighValueFields: z.array(z.string().min(1)).max(20),
		profileSummary: z.string().max(1_000),
	})
	.strict();
export type OperatingPreferenceAiState = z.infer<
	typeof OperatingPreferenceAiStateSchema
>;

export const OperatingPreferenceProfileV1Schema = z
	.object({
		aiState: OperatingPreferenceAiStateSchema,
		archetype: OperatingPreferenceArchetypeStateSchema,
		communicationStyle: OperatingPreferenceCommunicationStyleSchema,
		completionLevel: OperatingPreferenceCompletionLevelSchema,
		createdAt: z.string().datetime({ offset: true }),
		cryptoRailsPresentation: OperatingPreferenceCryptoRailsPresentationSchema,
		experience: OperatingPreferenceExperienceSchema,
		fieldSources: z.record(z.string(), OperatingPreferenceFieldSourceSchema),
		goals: z.array(OperatingPreferenceGoalSchema).max(5),
		intent: OperatingPreferenceIntentSchema,
		interests: OperatingPreferenceInterestsSchema,
		localization: OperatingPreferenceLocalizationSchema,
		moneyRhythm: OperatingPreferenceMoneyRhythmSchema,
		productMode: OperatingPreferenceProductModeSchema,
		schemaVersion: z.literal(OPERATING_PREFERENCE_PROFILE_SCHEMA_VERSION),
		updatedAt: z.string().datetime({ offset: true }),
		userId: z.string().min(1),
		volatilityComfort: OperatingPreferenceVolatilityComfortSchema,
	})
	.strict();
export type OperatingPreferenceProfileV1 = z.infer<
	typeof OperatingPreferenceProfileV1Schema
>;

export const OperatingPreferencePatchPathSchema = z.enum([
	"intent.primary",
	"intent.secondary",
	"intent.freeform",
	"goals",
	"moneyRhythm.preferredFundingBehavior",
	"moneyRhythm.startingAmount",
	"moneyRhythm.recurringAmount",
	"productMode.guidanceLevel",
	"productMode.automationLevel",
	"productMode.decisionStyle",
	"productMode.interfaceComplexity",
	"productMode.educationDepth",
	"volatilityComfort.level",
	"volatilityComfort.maxDrawdownComfort",
	"volatilityComfort.reactionToDrop",
	"volatilityComfort.preferenceLabel",
	"experience.overall",
	"experience.usedApps",
	"experience.assetFamiliarity",
	"interests.assets",
	"interests.features",
	"interests.cryptoInterest",
	"interests.excluded",
	"cryptoRailsPresentation.balanceDisplay",
	"cryptoRailsPresentation.showOnchainDetails",
	"cryptoRailsPresentation.showNetworkNames",
	"cryptoRailsPresentation.walletTransfers",
	"cryptoRailsPresentation.stablecoinLanguage",
	"cryptoRailsPresentation.cryptoNativeMode",
	"communicationStyle.tone",
	"communicationStyle.coachingStyle",
	"communicationStyle.notificationCadence",
	"communicationStyle.preferredChannels",
	"communicationStyle.digestFrequency",
	"archetype.current",
	"archetype.confidence",
	"aiState.profileSummary",
	"aiState.missingHighValueFields",
]);
export type OperatingPreferencePatchPath = z.infer<
	typeof OperatingPreferencePatchPathSchema
>;

export const OperatingPreferenceDraftPatchSchema = z
	.object({
		confidence: z.number().min(0).max(1),
		path: OperatingPreferencePatchPathSchema,
		source: OperatingPreferencePatchSourceSchema,
		status: OperatingPreferenceFieldStatusSchema,
		value: z.unknown(),
	})
	.strict();
export type OperatingPreferenceDraftPatch = z.infer<
	typeof OperatingPreferenceDraftPatchSchema
>;

export const OperatingPreferencePatchRecordSchema =
	OperatingPreferenceDraftPatchSchema.extend({
		appliedAt: z.string().datetime({ offset: true }),
		patchId: z.string().min(1),
	});
export type OperatingPreferencePatchRecord = z.infer<
	typeof OperatingPreferencePatchRecordSchema
>;

export function createDefaultOperatingPreferenceProfile(params: {
	now?: string;
	userId: string;
}): OperatingPreferenceProfileV1 {
	const now = params.now ?? new Date().toISOString();
	return {
		aiState: {
			confidence: 0.4,
			declaredFields: [],
			inferredFields: [],
			lastUpdatedAt: now,
			missingHighValueFields: [
				"intent.primary",
				"productMode.guidanceLevel",
				"volatilityComfort.level",
				"experience.overall",
				"cryptoRailsPresentation.balanceDisplay",
			],
			profileSummary:
				"Your setup is not complete yet. Answer a few prompts to personalize Glider.",
		},
		archetype: {
			confidence: 0,
			current: null,
			visibleToUser: true,
		},
		communicationStyle: {
			coachingStyle: "teacher",
			digestFrequency: "weekly",
			notificationCadence: "important_only",
			preferredChannels: ["push"],
			tone: "friendly",
		},
		completionLevel: "starter",
		createdAt: now,
		cryptoRailsPresentation: {
			balanceDisplay: "fiat_first",
			cryptoNativeMode: false,
			showNetworkNames: false,
			showOnchainDetails: false,
			stablecoinLanguage: "plain_english",
			walletTransfers: "hidden_until_requested",
		},
		experience: {
			assetFamiliarity: {
				crypto: "none",
				etfs: "none",
				options: "none",
				stablecoins: "none",
				stocks: "none",
			},
			overall: null,
			usedApps: [],
		},
		fieldSources: {},
		goals: [],
		intent: {
			freeform: null,
			primary: null,
			secondary: [],
		},
		interests: {
			assets: [],
			cryptoInterest: "none",
			excluded: [],
			features: [],
		},
		localization: {
			country: "US",
			currency: "USD",
			language: "en",
			region: null,
			source: "defaulted",
			timezone: null,
		},
		moneyRhythm: {
			preferredFundingBehavior: "exploring_first",
			recurringAmount: {
				amount: null,
				cadence: null,
				currency: "USD",
				enabled: false,
				isEstimate: true,
			},
			startingAmount: {
				amount: null,
				currency: "USD",
				isEstimate: true,
			},
		},
		productMode: {
			automationLevel: "suggested_with_confirmation",
			decisionStyle: "show_me_options",
			educationDepth: "plain_english",
			guidanceLevel: "guided",
			interfaceComplexity: "simple",
		},
		schemaVersion: OPERATING_PREFERENCE_PROFILE_SCHEMA_VERSION,
		updatedAt: now,
		userId: params.userId,
		volatilityComfort: {
			level: null,
			maxDrawdownComfort: null,
			preferenceLabel: null,
			reactionToDrop: null,
		},
	};
}
